import { useState, useEffect } from "react";
import { Box, Typography, Chip, Collapse, LinearProgress, Grid, Card, CardContent } from "@mui/material";
import { ChevronDown, ChevronUp, Lightbulb, Brain, Users, Compass, Code2, BookOpen, MessageSquare, CheckCircle2 } from "lucide-react";

const QB_LOADING_STEPS = [
  { label: "Reading job description…",              pct: 15 },
  { label: "Identifying role requirements…",        pct: 35 },
  { label: "Crafting technical questions…",         pct: 55 },
  { label: "Adding behavioral & situational…",      pct: 75 },
  { label: "Finalising question bank…",             pct: 90 },
];

function QuestionBankLoader() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStep((s) => Math.min(s + 1, QB_LOADING_STEPS.length - 1));
    }, 2800);
    return () => clearInterval(id);
  }, []);

  const { label, pct } = QB_LOADING_STEPS[step];

  return (
    <Card sx={{ minHeight: 400 }}>
      <CardContent sx={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        p: 6, textAlign: "center", minHeight: 400,
      }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "18px",
          background: "linear-gradient(135deg,#7c3aed,#2563eb)",
          display: "flex", alignItems: "center", justifyContent: "center", mb: 3,
          animation: "pulse 1.6s ease-in-out infinite",
          "@keyframes pulse": {
            "0%,100%": { transform: "scale(1)", opacity: 1 },
            "50%":      { transform: "scale(1.08)", opacity: 0.85 },
          },
        }}>
          <BookOpen size={32} color="#fff" />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
          Generating your question bank
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mb: 3, minHeight: 24, transition: "all 0.4s" }}>
          {label}
        </Typography>

        <Box sx={{ width: "100%", maxWidth: 320 }}>
          <LinearProgress
            variant="determinate" value={pct}
            sx={{
              height: 6, borderRadius: 3, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                background: "linear-gradient(90deg,#7c3aed,#2563eb)",
                borderRadius: 3, transition: "transform 0.8s ease",
              },
            }}
          />
          <Typography variant="caption" sx={{ color: "#94a3b8", mt: 1, display: "block" }}>
            This usually takes 10–20 seconds
          </Typography>
        </Box>

        {QB_LOADING_STEPS.map((s, i) => (
          <Box key={i} sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            mt: i === 0 ? 3 : 1, opacity: i <= step ? 1 : 0.3, transition: "opacity 0.4s",
          }}>
            <Box sx={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              bgcolor: i < step ? "#10b981" : i === step ? "#7c3aed" : "#e2e8f0",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background-color 0.4s",
            }}>
              {i < step && <CheckCircle2 size={12} color="#fff" />}
            </Box>
            <Typography variant="caption" sx={{
              color: i < step ? "#10b981" : i === step ? "#7c3aed" : "#94a3b8",
              fontWeight: i === step ? 700 : 400, transition: "color 0.4s",
            }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

const CATEGORY_META = {
  Technical:     { color: "#2563eb", bg: "#eff6ff",  Icon: Code2    },
  Behavioral:    { color: "#7c3aed", bg: "#f5f3ff",  Icon: Brain    },
  Situational:   { color: "#d97706", bg: "#fffbeb",  Icon: Compass  },
  "Culture Fit": { color: "#059669", bg: "#ecfdf5",  Icon: Users    },
};

const DIFFICULTY_META = {
  Easy:   { color: "#059669", bg: "#dcfce7" },
  Medium: { color: "#d97706", bg: "#fef9c3" },
  Hard:   { color: "#dc2626", bg: "#fee2e2" },
};

function QuestionCard({ q, index }) {
  const [open, setOpen] = useState(false);
  const cat  = CATEGORY_META[q.category]  || CATEGORY_META["Technical"];
  const diff = DIFFICULTY_META[q.difficulty] || DIFFICULTY_META["Medium"];
  const CatIcon = cat.Icon;

  return (
    <Box
      onClick={() => setOpen((p) => !p)}
      sx={{
        border: "1.5px solid",
        borderColor: open ? cat.color : "#e2e8f0",
        borderRadius: "14px",
        p: { xs: 2, md: 2.5 },
        cursor: "pointer",
        transition: "all 0.2s",
        bgcolor: open ? cat.bg : "#fff",
        "&:hover": { borderColor: cat.color, boxShadow: `0 4px 16px ${cat.color}18` },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{
          minWidth: 28, height: 28, borderRadius: "8px",
          bgcolor: open ? "#fff" : cat.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, mt: 0.1, transition: "all 0.2s",
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: cat.color }}>{index + 1}</Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 13, md: 14 }, fontWeight: 600, color: "#0f172a", lineHeight: 1.5 }}>
            {q.question}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
            <Chip
              icon={<CatIcon size={11} color={cat.color} />}
              label={q.category}
              size="small"
              sx={{
                bgcolor: open ? "#fff" : cat.bg, color: cat.color, fontWeight: 700, fontSize: 11,
                border: `1px solid ${cat.color}30`, height: 22,
                "& .MuiChip-icon": { ml: "6px" },
              }}
            />
            <Chip
              label={q.difficulty}
              size="small"
              sx={{
                bgcolor: diff.bg, color: diff.color, fontWeight: 700, fontSize: 11,
                border: `1px solid ${diff.color}30`, height: 22,
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0, color: "#94a3b8", mt: 0.3 }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Box>
      </Box>

      {/* ── Expanded: Tip + Sample Answer ── */}
      <Collapse in={open}>
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: `${cat.color}22`, display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Tip */}
          {q.tip && (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{
                minWidth: 28, height: 28, borderRadius: "8px", bgcolor: "#fefce8",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Lightbulb size={14} color="#d97706" />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#d97706", mb: 0.4, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Answer Tip
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.6 }}>
                  {q.tip}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Sample Answer */}
          {q.sampleAnswer && (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{
                minWidth: 28, height: 28, borderRadius: "8px", bgcolor: cat.bg,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <MessageSquare size={14} color={cat.color} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: cat.color, mb: 0.4, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Sample Answer
                </Typography>
                <Box sx={{
                  bgcolor: "#fff", borderRadius: "10px",
                  border: `1px solid ${cat.color}20`, p: 1.5,
                }}>
                  <Typography sx={{ fontSize: 13, color: "#334155", lineHeight: 1.7, fontStyle: "italic" }}>
                    "{q.sampleAnswer}"
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function QuestionBank({ data, loading }) {
  if (loading) return <QuestionBankLoader />;

  if (!data) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 10, gap: 2, textAlign: "center" }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: "16px",
          background: "linear-gradient(135deg,#f5f3ff,#eff6ff)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <BookOpen size={28} color="#7c3aed" />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>Interview Question Bank</Typography>
        <Typography sx={{ color: "#64748b", fontSize: 14, maxWidth: 340, lineHeight: 1.6 }}>
          Paste a job description and click "Generate Questions" to get 10 role-specific
          interview questions — with expert tips and sample answers.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mt: 1 }}>
          {Object.entries(CATEGORY_META).map(([name, m]) => {
            const Icon = m.Icon;
            return (
              <Chip
                key={name}
                icon={<Icon size={11} color={m.color} />}
                label={name}
                size="small"
                sx={{
                  bgcolor: m.bg, color: m.color, fontWeight: 600, fontSize: 11,
                  border: `1px solid ${m.color}30`, height: 24,
                  "& .MuiChip-icon": { ml: "6px" },
                }}
              />
            );
          })}
        </Box>
        {/* Distribution hint */}
        <Box sx={{ mt: 1, px: 2.5, py: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: 12, color: "#64748b", lineHeight: 1.8, textAlign: "left" }}>
            📐 <strong>Distribution:</strong> 5 Technical · 2 Behavioral · 2 Situational · 1 Culture Fit
          </Typography>
        </Box>
      </Box>
    );
  }

  const questions = data.questions || [];
  const byCategory = questions.reduce((acc, q) => {
    acc[q.category] = (acc[q.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3, p: { xs: 2, md: 2.5 },
        borderRadius: "14px",
        background: "linear-gradient(135deg,#f5f3ff 0%,#eff6ff 100%)",
        border: "1.5px solid #e0e7ff",
      }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: 16, md: 18 }, color: "#0f172a", mb: 0.5 }}>
          {data.role || "Interview"} Question Bank
        </Typography>
        {data.companyName && (
          <Typography sx={{ fontSize: 13, color: "#7c3aed", fontWeight: 600, mb: 1 }}>
            {data.companyName}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          <Chip label={`${questions.length} Questions`} size="small"
            sx={{ bgcolor: "#fff", fontWeight: 700, fontSize: 11, border: "1px solid #e0e7ff", height: 22 }} />
          {Object.entries(byCategory).map(([cat, count]) => {
            const m = CATEGORY_META[cat] || {};
            return (
              <Chip key={cat} label={`${count} ${cat}`} size="small"
                sx={{
                  bgcolor: m.bg || "#f1f5f9", color: m.color || "#64748b",
                  fontWeight: 600, fontSize: 11, border: `1px solid ${(m.color || "#64748b")}30`, height: 22,
                }} />
            );
          })}
        </Box>
        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 1.5 }}>
          Tap any question to reveal the answer tip and a sample answer.
        </Typography>
      </Box>

      {/* Questions list */}
      <Grid container spacing={1.5}>
        {questions.map((q, i) => (
          <Grid item xs={12} key={q.id ?? i}>
            <QuestionCard q={q} index={i} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
